import React, { useEffect, useState } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { apiHandler } from '../../api/apihandler';
import { TEACHERS_LUNCH_PLAN_WAIVER_API } from '../../api/constants';
import { formatDisplayDate } from '../../utilities/displayFormat';

// Shared "Lunch Plan Waiver" modal - the real one, previously only reachable
// from the Attendance page's per-student action button. Also rendered from
// notification.js so a teacher clicking a Lunch Plan Waiver notification
// sees this exact modal instead of a separate, limited one. Self-contained:
// fetches its own data from studentId rather than depending on the caller's
// other state.
const LunchPlanWaiverModal = ({ show, onHide, studentId, studentName }) => {
  const [lunchPlanData, setLunchPlanData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !studentId) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLunchPlanData(null);
      try {
        const response = await apiHandler(TEACHERS_LUNCH_PLAN_WAIVER_API, 'POST', {
          student_id: studentId,
        });
        if (cancelled) return;
        if (response.data?.response?.status === 200 && response.data?.data?.waiver) {
          setLunchPlanData({
            ...response.data.data.waiver,
            student: response.data.data.student,
          });
        }
      } catch (error) {
        console.error('Error loading lunch plan:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, studentId]);

  const formatDate = formatDisplayDate;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '16px 20px', background: 'white' }}>
        <Modal.Title style={{ fontSize: '15px', fontWeight: '700', color: '#111827', lineHeight: '1.3', width: '100%', maxWidth: 'calc(100% - 40px)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ri-restaurant-line" style={{ fontSize: '18px', color: '#10b981' }}></i>
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
              Lunch Plan Waiver
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
              {lunchPlanData?.student?.name || studentName || 'Student'} • Dietary Documentation
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px', maxHeight: 'calc(75vh - 140px)', overflowY: 'auto', background: '#f9fafb' }}>
        {lunchPlanData ? (
          <div>
            {/* Waiver Information Section */}
            <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
              <h6 style={{ color: '#374151', fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="ri-file-list-3-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                Waiver Information
              </h6>
              {lunchPlanData.fillup_date && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Submitted Date</div>
                  <div style={{ fontSize: '13px', color: '#111827' }}>{formatDate(lunchPlanData.fillup_date)}</div>
                </div>
              )}
              {lunchPlanData.reason && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Reason</div>
                  <div style={{ fontSize: '13px', color: '#111827', background: '#f9fafb', padding: '12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                    {lunchPlanData.reason}
                  </div>
                </div>
              )}
            </div>

            {/* Signatures Section */}
            <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
              <h6 style={{ color: '#374151', fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="ri-quill-pen-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                Signatures
              </h6>
              <Row>
                <Col md="6">
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>Parent Signature</div>
                    {lunchPlanData.parents_signature ? (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', backgroundColor: '#f9fafb', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                          src={lunchPlanData.parents_signature}
                          alt="Parent Signature"
                          style={{ maxWidth: '100%', maxHeight: '80px', display: 'block', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div style={{ display: 'none', color: '#6b7280', textAlign: 'center' }}>
                          <i className="ri-image-line" style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}></i>
                          <small style={{ fontSize: '11px' }}>Image not available</small>
                        </div>
                      </div>
                    ) : (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', backgroundColor: '#f9fafb', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ color: '#6b7280', textAlign: 'center' }}>
                          <i className="ri-file-text-line" style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}></i>
                          <small style={{ fontSize: '11px' }}>No signature provided</small>
                        </div>
                      </div>
                    )}
                    {lunchPlanData.parent_signature_date && (
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>
                        Signed: {formatDate(lunchPlanData.parent_signature_date)}
                      </div>
                    )}
                  </div>
                </Col>
                <Col md="6">
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>Supervisor Signature</div>
                    {lunchPlanData.supervisor_signature ? (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', backgroundColor: '#f9fafb', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                          src={lunchPlanData.supervisor_signature}
                          alt="Supervisor Signature"
                          style={{ maxWidth: '100%', maxHeight: '80px', display: 'block', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div style={{ display: 'none', color: '#6b7280', textAlign: 'center' }}>
                          <i className="ri-image-line" style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}></i>
                          <small style={{ fontSize: '11px' }}>Image not available</small>
                        </div>
                      </div>
                    ) : (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', backgroundColor: '#f9fafb', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ color: '#6b7280', textAlign: 'center' }}>
                          <i className="ri-file-text-line" style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}></i>
                          <small style={{ fontSize: '11px' }}>No signature provided</small>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Additional waiver details if available */}
            {lunchPlanData.student && (
              <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h6 style={{ color: '#374151', fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="ri-user-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                  Student Information
                </h6>
                <Row>
                  <Col md="6">
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Name</div>
                      <div style={{ fontSize: '13px', color: '#111827' }}>{lunchPlanData.student.name}</div>
                    </div>
                  </Col>
                  <Col md="6">
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Email</div>
                      <div style={{ fontSize: '13px', color: '#111827' }}>{lunchPlanData.student.email}</div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6c757d' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="text-center p-4 text-muted">
            No lunch plan waiver found{studentName ? ` for ${studentName}` : ''}.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LunchPlanWaiverModal;
